import { useContext } from 'react';
import {
  displayNameFor,
  Terms,
  termsFor,
} from '@stormwater-software/lib-domain';
import AppContext from '../contexts/AppContext';

function useTenantType(): string | undefined {
  const app = useContext(AppContext) as any;
  const user = app?.state?.user;
  const tenant = user?.tenants?.find((t: any) => t.id === user.tenantId);

  return tenant?.tenant_type;
}

/**
 * SW2-2364 — the word the current tenant uses for what the codebase calls a
 * "client": Permittee (regulator), Market (builder) or Client (consultant).
 *
 * Display text only. API paths, permission resources, role names and
 * feature-flag names keep "client" — never pass them through these terms.
 *
 * The tenant type arrives with the profile AppContext loads at bootstrap, so
 * this makes no request. With no profile (public/guest pages, offline before
 * the first profile load) it falls back to the consultant terms, which is what
 * every tenant saw before tenant types existed.
 */
export function useTerminology(): Terms {
  return termsFor(useTenantType());
}

/**
 * SW2-2366 — display text for a stored role or feature-flag name, e.g.
 * "Client Manager" shows as "Permittee Manager" to a regulator.
 *
 * The stored name stays the value everywhere it is compared or sent back to
 * the API; only what is rendered goes through this. For select options, add a
 * separate label field (see `withDisplayNames`) rather than rewriting `name`.
 */
export function useDisplayName(): (name: string) => string {
  const tenantType = useTenantType();

  return (name: string) => (name ? displayNameFor(name, tenantType) : name);
}

/**
 * Adds a `displayName` label to role/feature options so a select can show it
 * via `labelKey: 'displayName'` while `name` stays the raw, compared value.
 */
export function withDisplayNames<T extends { name: string }>(
  options: T[] | undefined,
  displayName: (name: string) => string,
): Array<T & { displayName: string }> | undefined {
  return options?.map((option) => ({
    ...option,
    displayName: displayName(option.name),
  }));
}

/**
 * Copy for the entity-picker select that several forms repeat. Only the words
 * are shared — each form keeps its own control type and flags.
 */
export function entitySelectCopy(
  terms: Terms,
  variant: 'plain' | 'report' = 'plain',
) {
  return {
    label:
      variant === 'report' ? `Select ${terms.entity}(s)` : terms.entityPlural,
    noOptionMessage: `No ${terms.entityPlural} found`,
    placeholder: `Search for ${terms.entityPlural}`,
  };
}

export default useTerminology;
